import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Slider,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetZoomIcon,
  RotateRight as RotateIcon,
} from '@mui/icons-material';

const MIN_ZOOM = 25;
const MAX_ZOOM = 300;
const ZOOM_STEP = 25;

const DocumentPreview = ({ open, onClose, blobUrl, fileName, mimeType, onDownload }) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const posStart = useRef({ x: 0, y: 0 });

  const isPdf = mimeType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf');
  const isImage =
    mimeType?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName || '');

  const handleZoomIn = () => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
  const handleResetZoom = () => {
    setZoom(100);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom((z) => {
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        return Math.max(MIN_ZOOM, Math.min(z + delta, MAX_ZOOM));
      });
    }
  }, []);

  const handleMouseDown = (e) => {
    if (zoom > 100) {
      setDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY };
      posStart.current = { ...position };
    }
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      setPosition({
        x: posStart.current.x + (e.clientX - dragStart.current.x),
        y: posStart.current.y + (e.clientY - dragStart.current.y),
      });
    }
  };

  const handleMouseUp = () => setDragging(false);

  const handleClose = () => {
    setZoom(100);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setFullScreen(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="document-preview-title"
      PaperProps={{
        sx: {
          height: fullScreen ? '100vh' : '85vh',
          maxHeight: fullScreen ? '100vh' : '85vh',
        },
      }}
    >
      {/* Title bar */}
      <DialogTitle
        id="document-preview-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 2,
          backgroundColor: 'grey.900',
          color: 'white',
        }}
      >
        <Typography variant="body2" noWrap sx={{ maxWidth: '40%', fontWeight: 500 }}>
          {fileName || 'Document Preview'}
        </Typography>

        {/* Zoom controls - only for images */}
        {isImage && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Zoom Out">
              <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM} sx={{ color: 'grey.300' }}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Slider
              value={zoom}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              onChange={(_, val) => setZoom(val)}
              sx={{
                width: 120,
                color: 'grey.400',
                '& .MuiSlider-thumb': { width: 14, height: 14 },
              }}
            />
            <Tooltip title="Zoom In">
              <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM} sx={{ color: 'grey.300' }}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ color: 'grey.400', minWidth: 40, textAlign: 'center' }}>
              {zoom}%
            </Typography>
            <Tooltip title="Rotate">
              <IconButton size="small" onClick={handleRotate} sx={{ color: 'grey.300' }}>
                <RotateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset">
              <IconButton size="small" onClick={handleResetZoom} sx={{ color: 'grey.300' }}>
                <ResetZoomIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={fullScreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={() => setFullScreen((p) => !p)} sx={{ color: 'grey.300' }}>
              {fullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={handleClose} sx={{ color: 'grey.300' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content area */}
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 0,
          backgroundColor: 'grey.800',
          overflow: 'hidden',
          cursor: isImage && zoom > 100 ? (dragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
        }}
        onWheel={isImage ? handleWheel : undefined}
        onMouseDown={isImage ? handleMouseDown : undefined}
        onMouseMove={isImage ? handleMouseMove : undefined}
        onMouseUp={isImage ? handleMouseUp : undefined}
        onMouseLeave={isImage ? handleMouseUp : undefined}
      >
        {isPdf && blobUrl && (
          <iframe
            src={blobUrl}
            title={fileName || 'PDF Preview'}
            sandbox="allow-same-origin"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        )}

        {isImage && blobUrl && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <img
              src={blobUrl}
              alt={fileName || 'Document'}
              draggable={false}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom / 100}) rotate(${rotation}deg)`,
                transition: dragging ? 'none' : 'transform 0.2s ease',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}

        {!isPdf && !isImage && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="grey.400" gutterBottom>
              Preview not available for this file type.
            </Typography>
            <Typography variant="body2" color="grey.500">
              Please download the file to view it.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Bottom actions */}
      <DialogActions sx={{ backgroundColor: 'grey.900', px: 2, py: 1 }}>
        {isImage && (
          <Typography variant="caption" color="grey.500" sx={{ mr: 'auto' }}>
            Ctrl + Scroll to zoom
          </Typography>
        )}
        <Button onClick={handleClose} sx={{ color: 'grey.400' }}>
          Close
        </Button>
        {onDownload && (
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreview;
